import { io, type Socket } from "socket.io-client";
import { $, routeLabel } from "./common";

interface EventItem {
  id: string;
  name: string;
  cameras: number;
  createdAt: string;
  online?: number;
  // Закрыта паролем для этого браузера / пароль вообще задан.
  locked?: boolean;
  hasPassword?: boolean;
}

type SlotKind = "phone" | "pro";

interface SlotLinks {
  slot: number;
  kind: SlotKind;
  name: string;
  camUrl: string;
  outUrl: string;
  qr: string;
}

interface SlotStatus {
  slot: number;
  online: boolean;
  since: number | null;
  viewers: number;
  stats: { width: number; height: number; fps: number; kbps: number; codec: string; encoder?: string; capture?: string; version?: string; model?: string; limitation: string; route: string; rtt: number | null; bwe: number | null; target: number; battery: number | null; device: string } | null;
}

const app = $("#app");
// Режим сервиса: site — сайт-посредник (пульта нет), studio — программа на компьютере режиссёра, all — всё в одном.
let me: { mode?: string; site?: string; build?: string; version?: string; siteVersion?: string } = {};

// Версия — компактно в правом верхнем углу. Программа старше сайта — подсказываем обновить.
function versionBadge() {
  const v = me.version ?? "";
  if (!v) return "";
  const old = me.mode === "studio" && me.siteVersion && me.siteVersion !== v;
  return old
    ? `<span class="ver warn" title="Программа ${esc(v)}, сайт уже ${esc(me.siteVersion!)} — возьмите новую версию программы">${esc(v)} · сайт ${esc(me.siteVersion!)}</span>`
    : `<span class="ver" title="${me.mode === "studio" ? `Aircut Studio ${esc(v)}, сборка ${esc(me.build ?? "")}` : `Версия ${esc(v)}`}">${esc(v)}</span>`;
}
let opSocket: Socket | null = null;
let clockTimer = 0;

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

class LockedError extends Error {}

// ——— Синхронизация: одно подключение к серверу на всю страницу ———
// Сервер сообщает «трансляции изменились» (их создали, удалили, переименовали, сменили пароль или ссылки
// на любом устройстве) — список и открытая трансляция обновляются сами, без перезагрузки.
let watching = "";
let pageBuild = "";
let notice = "";

function ensureSocket(): Socket {
  if (opSocket) return opSocket;
  const s = io({ auth: { role: "op" }, transports: ["websocket"], reconnectionDelay: 1000, reconnectionDelayMax: 5000 });
  s.on("connect", () => watching && s.emit("watch-event", watching));
  s.on("snapshot", paintStatus);
  s.on("auth-error", () => renderLogin());
  s.on("events-changed", (msg: { id?: string }) => onEventsChanged(String(msg?.id ?? "")));
  // Сервер обновился с тех пор, как открыли страницу, — берём новую версию страницы.
  s.on("hello", (msg: { build?: string }) => {
    const build = String(msg?.build ?? "");
    if (!pageBuild) pageBuild = build;
    else if (build && build !== pageBuild && !document.activeElement?.closest("#app input, #app textarea")) location.reload();
  });
  opSocket = s;
  return s;
}

function watchEvent(id: string) {
  watching = id;
  const s = ensureSocket();
  if (id) s.emit("watch-event", id);
  else s.emit("unwatch");
}

let changeTimer = 0;
function onEventsChanged(id: string) {
  clearTimeout(changeTimer);
  changeTimer = window.setTimeout(() => refreshAfterChange(id), 300);
}

async function refreshAfterChange(id: string) {
  // Оператор что-то вводит — не сбиваем, обновим чуть позже.
  if (document.activeElement?.closest("#app input, #app select, #app textarea")) {
    changeTimer = window.setTimeout(() => refreshAfterChange(id), 2000);
    return;
  }
  const m = location.hash.match(/^#\/e\/([A-Za-z0-9_-]+)/);
  if (!m) return void renderList().catch(() => {});
  if (id && id !== m[1]) return;
  const res = await fetch(`/api/events/${m[1]}`, { headers: { "Content-Type": "application/json" } });
  if (res.status === 404) {
    notice = "Эту трансляцию удалили.";
    location.hash = "#/";
    return;
  }
  if (res.status === 403) return renderUnlock(m[1], "Пароль трансляции сменили — введите новый");
  // Изменилась именно эта трансляция (название, камеры, ссылки) — перерисовываем.
  if (id) route();
}

async function api<T>(url: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init.headers ?? {}) },
  });
  if (res.status === 401) {
    renderLogin();
    throw new Error("unauthorized");
  }
  if (res.status === 403) throw new LockedError("locked");
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? `HTTP ${res.status}`);
  return res.json();
}

// Трансляция закрыта паролем: форма ввода. Пароль знают только те, кто работает с этой трансляцией.
function renderUnlock(id: string, error = "") {
  watchEvent("");
  app.innerHTML = `
    ${header(true)}
    <form class="card login" id="unlock">
      <h1>🔒 Трансляция закрыта</h1>
      <p class="muted">Введите пароль этой трансляции — его задали при создании. Спросите у того, кто её ведёт.</p>
      <label class="field"><span>Пароль трансляции</span><input type="password" name="password" autocomplete="off" required autofocus></label>
      ${error ? `<p class="error">${esc(error)}</p>` : ""}
      <button class="btn primary" type="submit">Открыть</button>
    </form>`;
  bindLogout();
  ($("#unlock") as HTMLFormElement).onsubmit = async (e) => {
    e.preventDefault();
    const password = new FormData(e.target as HTMLFormElement).get("password");
    const res = await fetch(`/api/events/${id}/unlock`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password }) });
    if (res.ok) return route();
    renderUnlock(id, (await res.json().catch(() => ({}))).error ?? "Не получилось");
  };
}

// Смена пароля трансляции — на её странице. Все, кто входил со старым паролем, теряют доступ.
function openPasswordForm(id: string, hasPassword: boolean) {
  const box = $("#pass-box");
  if (!box.hidden) {
    box.hidden = true;
    return;
  }
  box.hidden = false;
  box.innerHTML = `
    <form class="row" id="pass-form">
      ${hasPassword ? `<label class="field"><span>Текущий пароль</span><input type="password" name="current" autocomplete="off" required></label>` : ""}
      <label class="field"><span>Новый пароль (от 4 символов)</span><input type="password" name="password" minlength="4" maxlength="100" autocomplete="new-password" required></label>
      <label class="field"><span>Ещё раз</span><input type="password" name="again" minlength="4" maxlength="100" autocomplete="new-password" required></label>
      <button class="btn primary" type="submit">Сменить пароль</button>
      <span class="error" id="pass-err"></span>
    </form>
    <p class="muted">После смены все, кто открыл трансляцию со старым паролем (страница QR, режиссёрский пульт), будут отключены и введут новый. Камеры и vMix это не затрагивает — у них свои ссылки.</p>`;
  ($("#pass-form") as HTMLFormElement).onsubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    const err = $("#pass-err");
    if (fd.get("password") !== fd.get("again")) return void (err.textContent = "Пароли не совпадают");
    try {
      await api(`/api/events/${id}/password`, { method: "POST", body: JSON.stringify({ current: fd.get("current"), password: fd.get("password") }) });
      box.innerHTML = `<p class="live-text">Пароль трансляции изменён.</p>`;
      $("#pass-btn").textContent = "Сменить пароль";
    } catch (ex) {
      err.textContent = (ex as Error).message;
    }
  };
}

function renderLogin(error = "") {
  opSocket?.disconnect();
  opSocket = null;
  watching = "";
  app.innerHTML = `
    <div class="login-ver">${versionBadge()}</div>
    <form class="card login" id="login">
      <h1>${me.mode === "studio" ? "Aircut Studio" : "Aircut"}</h1>
      <p class="muted">${me.mode === "studio" ? `Вход на сайт ${esc(me.site ?? "")} — трансляции, пароли и камеры там, эфир сводится на этом компьютере` : "Телефоны как HD-камеры для vMix"}</p>
      <label class="field"><span>${me.mode === "studio" ? "Пароль оператора сайта" : "Пароль оператора"}</span><input type="password" name="password" autocomplete="current-password" required autofocus></label>
      ${error ? `<p class="error">${esc(error)}</p>` : ""}
      <button class="btn primary" type="submit">Войти</button>
    </form>`;
  ($("#login") as HTMLFormElement).onsubmit = async (e) => {
    e.preventDefault();
    const password = new FormData(e.target as HTMLFormElement).get("password");
    const res = await fetch("/api/login", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password }) });
    if (res.ok) route();
    else renderLogin(res.status === 429 ? "Слишком много попыток, подождите 15 минут" : res.status === 502 ? (await res.json().catch(() => ({}))).error ?? "Сайт не отвечает" : "Неверный пароль");
  };
}

function header(back: boolean) {
  return `
    <header class="topbar">
      ${back ? `<a class="btn ghost" href="#/">Все трансляции</a>` : `<strong>${me.mode === "studio" ? `Aircut Studio <span class="muted">· сайт ${esc(me.site ?? "")}</span>` : "Пульт трансляции"}</strong>`}
      <span class="topbar-right">${versionBadge()}${me.mode === "studio" ? `<button class="btn ghost" id="quit" title="Закрыть Aircut Studio целиком: движки и саму программу (чёрное окно)">Закрыть программу</button>` : ""}<button class="btn ghost" id="logout">Выйти</button></span>
    </header>`;
}

function bindQuit() {
  const b = document.querySelector<HTMLButtonElement>("#quit");
  if (!b) return;
  b.onclick = async () => {
    if (!confirm("Закрыть программу Aircut Studio? Если идёт эфир или запись — они остановятся. Запустить снова — Aircut-Studio.exe.")) return;
    const res = await fetch("/api/quit", { method: "POST" });
    if (!res.ok) return alert((await res.json().catch(() => ({}))).error ?? "Не получилось");
    opSocket?.disconnect();
    opSocket = null;
    app.innerHTML = `<section class="card login"><h1>Aircut Studio закрыта</h1><p class="muted">Эту вкладку можно закрыть. Чтобы начать снова — запустите Aircut-Studio.exe.</p></section>`;
  };
}

function bindLogout() {
  bindQuit();
  $("#logout").onclick = async () => {
    await fetch("/api/logout", { method: "POST" });
    renderLogin();
  };
}

async function renderList() {
  const events = await api<EventItem[]>("/api/events");
  watchEvent("");
  const note = notice;
  notice = "";
  app.innerHTML = `
    ${header(false)}
    ${note ? `<p class="card warn">${esc(note)}</p>` : ""}
    <section class="card">
      <h2>Новая трансляция</h2>
      <form id="create" class="row">
        <label class="field grow"><span>Название</span><input name="name" placeholder="Финал, 18 октября" maxlength="80" required></label>
        <label class="field"><span>Камер</span><input name="cameras" type="number" min="1" max="8" value="4" required></label>
        <label class="field"><span>Пароль трансляции</span><input name="password" type="password" minlength="4" maxlength="100" autocomplete="new-password" placeholder="от 4 символов" required></label>
        <button class="btn primary" type="submit">Создать</button>
      </form>
      <p class="muted">Без этого пароля трансляцию, её QR-коды и режиссёрский пульт не откроют даже другие операторы. Сменить его можно на странице трансляции.</p>
      <p class="error" id="create-err"></p>
    </section>
    <section class="list">
      ${
        events.length
          ? events
              .map(
                (e) => `
        <a class="card event" href="#/e/${e.id}">
          <div><strong>${e.locked ? "🔒 " : ""}${esc(e.name)}</strong><div class="muted">${new Date(e.createdAt).toLocaleString("ru-RU")} · камер: ${e.cameras}${e.hasPassword ? "" : ` · <span class="warn">без пароля</span>`}</div></div>
          ${e.locked ? `<span class="pill">нужен пароль</span>` : `<span class="pill ${e.online ? "live" : ""}">${e.online ? `в эфире ${e.online}` : "нет камер"}</span>`}
        </a>`,
              )
              .join("")
          : `<p class="muted">Трансляций пока нет — создайте первую.</p>`
      }
    </section>`;
  bindLogout();
  ($("#create") as HTMLFormElement).onsubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    try {
      const created = await api<EventItem>("/api/events", {
        method: "POST",
        body: JSON.stringify({ name: fd.get("name"), cameras: Number(fd.get("cameras")), password: fd.get("password") }),
      });
      location.hash = `#/e/${created.id}`;
    } catch (ex) {
      $("#create-err").textContent = (ex as Error).message;
    }
  };
}

function previewSrc(outUrl: string) {
  const u = new URL(outUrl);
  // Студия: картинку показывает своя страница, а к камере подключается через сайт.
  const sig = me.mode === "studio" && me.site ? `&sig=${encodeURIComponent(me.site)}` : "";
  return `${u.pathname}${u.search}&muted=1&label=1&debug=1${sig}`;
}

const openPreviews = new Set<number>();

function syncPreviews() {
  document.querySelectorAll<HTMLElement>(".cam").forEach((card) => {
    const on = openPreviews.has(Number(card.dataset.slot));
    card.classList.toggle("preview-open", on);
    card.querySelector<HTMLElement>("[data-preview]")!.textContent = on ? "Скрыть картинку" : "Показать картинку";
    const box = card.querySelector<HTMLElement>(".preview")!;
    box.hidden = !on;
    const frame = box.querySelector("iframe");
    if (on && !frame) {
      const f = document.createElement("iframe");
      f.src = box.dataset.out ?? "";
      f.allow = "autoplay";
      f.title = "Картинка с камеры";
      box.replaceChildren(f, Object.assign(document.createElement("span"), { className: "preview-hint", textContent: "клик — звук вкл/выкл" }));
    } else if (!on && frame) {
      box.replaceChildren();
    }
  });
  const btn = $("#previews");
  btn.hidden = openPreviews.size === 0;
  btn.textContent = `Скрыть картинки (${openPreviews.size})`;
}

const KIND_TEXT: Record<SlotKind, { title: string; link: string; kick: string; kickAsk: string; cpu: string }> = {
  phone: {
    title: "Камера",
    link: "Ссылка для телефона",
    kick: "Отключить телефон",
    kickAsk: "телефон",
    cpu: "Телефон не успевает кодировать",
  },
  pro: {
    title: "Проф. камера",
    link: "Ссылка для ноутбука (открыть в Chrome)",
    kick: "Отключить ноутбук",
    kickAsk: "ноутбук",
    cpu: "Ноутбук не успевает кодировать — закройте лишние программы или выберите 720p",
  },
};

const kindOf = (card: HTMLElement): SlotKind => (card.dataset.kind === "pro" ? "pro" : "phone");

function camTitle(slot: number, kind: SlotKind, name: string) {
  return name || `${KIND_TEXT[kind].title} ${slot}`;
}

// Меняем карточку на месте, без перерисовки пульта, чтобы не закрывать открытые картинки.
function paintSlot(card: HTMLElement, s: Pick<SlotLinks, "slot" | "kind" | "name" | "camUrl">) {
  const t = KIND_TEXT[s.kind];
  card.dataset.kind = s.kind;
  card.querySelector("h2")!.textContent = camTitle(s.slot, s.kind, s.name);
  card.querySelector<HTMLElement>("[data-cam-label]")!.textContent = t.link;
  card.querySelector<HTMLInputElement>("[data-cam-url]")!.value = s.camUrl;
  card.querySelector<HTMLElement>("[data-kick]")!.textContent = t.kick;
  card.querySelector<HTMLElement>(".qr")!.hidden = s.kind === "pro";
  card.querySelector<HTMLElement>("[data-open-cam]")!.hidden = s.kind !== "pro";
  card.querySelector<HTMLAnchorElement>("[data-open-cam]")!.href = s.camUrl;
}

function formatUptime(since: number) {
  const s = Math.max(0, Math.floor((Date.now() - since) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h ? `${h}:` : ""}${String(m).padStart(h ? 2 : 1, "0")}:${String(s % 60).padStart(2, "0")}`;
}

function paintStatus(list: SlotStatus[]) {
  for (const st of list) {
    const card = document.querySelector<HTMLElement>(`[data-slot="${st.slot}"]`);
    if (!card) continue;
    card.classList.toggle("is-live", st.online);
    const pill = card.querySelector<HTMLElement>(".pill")!;
    pill.textContent = st.online ? "камера в сети" : "нет камеры";
    pill.classList.toggle("live", st.online);
    card.querySelector<HTMLElement>("[data-kick]")!.hidden = !st.online;
    card.dataset.since = st.since ? String(st.since) : "";
    const s = st.stats;
    const lines: string[] = [];
    if (st.online && s && s.width) {
      lines.push(`${s.width}×${s.height} · ${s.fps} к/с · ${(s.kbps / 1000).toFixed(1)} Мбит/с · ${esc(s.codec)}`);
      const phone = [s.model, s.encoder && `кодер ${s.encoder}`, s.capture === "copy" && "копия кадров"].filter(Boolean).join(" · ");
      if (phone) lines.push(`Телефон: ${esc(phone)}`);
      const current = me.siteVersion || me.version;
      if (current && s.version !== current)
        lines.push(`<span class="warn">На телефоне старая версия страницы (${esc(s.version || "до v1.8")}) — остановите трансляцию на телефоне и обновите страницу</span>`);
      if (s.route) lines.push(`Связь: ${routeLabel(s.route)}${s.rtt != null ? ` · пинг ${s.rtt} мс` : ""}`);
      if (s.limitation === "network" && s.bwe != null)
        lines.push(`<span class="warn">Слабая сеть: канал ${(s.bwe / 1000).toFixed(1)} из нужных ${(s.target / 1000).toFixed(0)} Мбит/с</span>`);
      if (s.limitation === "bandwidth") lines.push(`<span class="warn">Не хватает интернета — качество снижено</span>`);
      if (s.limitation === "cpu") lines.push(`<span class="warn">${KIND_TEXT[kindOf(card)].cpu}</span>`);
    } else if (st.online) {
      lines.push("Камера включена, vMix ещё не подключён");
    }
    if (st.online && s?.device && kindOf(card) === "pro") lines.push(`Источник: ${esc(s.device)}`);
    if (st.online && s?.battery != null && (kindOf(card) === "phone" || s.battery < 1)) lines.push(`Батарея ${Math.round(s.battery * 100)}%`);
    lines.push(`Приёмников: ${st.viewers}`);
    card.querySelector<HTMLElement>(".stats")!.innerHTML = lines.join("<br>");
  }
}

async function renderEvent(id: string) {
  const { event, slots } = await api<{ event: EventItem; slots: SlotLinks[] }>(`/api/events/${id}`);
  app.innerHTML = `
    ${header(true)}
    <section class="event-head">
      <h1>${esc(event.name)}</h1>
      <div class="row">
        ${
          me.mode === "site"
            ? `<span class="pill" title="Режиссёрский пульт работает в программе Aircut Studio на компьютере режиссёра: войдите в ней на этот сайт и откройте эту трансляцию">Пульт — в программе Aircut Studio</span>`
            : `<a class="btn primary" href="/studio/${event.id}">Режиссёрский пульт</a>`
        }
        <button class="btn ghost" id="rename">Переименовать</button>
        <button class="btn ghost" id="cams">Камер: ${event.cameras}</button>
        <button class="btn ghost" id="previews"></button>
        <button class="btn ghost" id="rotate">Сменить ссылки</button>
        <button class="btn ghost" id="pass-btn">${event.hasPassword ? "Сменить пароль" : "⚠ Задать пароль"}</button>
        <button class="btn danger" id="delete">Удалить</button>
      </div>
    </section>
    ${event.hasPassword ? "" : `<p class="card warn">У этой трансляции нет пароля — её может открыть любой оператор. Задайте пароль.</p>`}
    <section class="card" id="pass-box" hidden></section>
    <details class="card howto">
      <summary>Как подключить к vMix</summary>
      <ol>
        <li>На каждом телефоне откройте QR-код своей камеры (камера телефона или Google Lens). Сертификат самоподписанный — нажмите «Дополнительно → Перейти на сайт».</li>
        <li>Поверните телефон горизонтально и нажмите «Начать трансляцию». Экран не гасите, приложение не сворачивайте.</li>
        <li>В vMix: <b>Add Input → Web Browser</b>, вставьте «Ссылку для vMix», размер <b>1920×1080</b>. Для звука включите аудио у этого входа.</li>
        <li>Повторите для каждой камеры — каждый телефон станет отдельным входом vMix.</li>
        <li><b>Проф. камера через ноутбук.</b> Подключите камеру к ноутбуку через карту захвата HDMI/SDI (Cam Link, USB-HDMI на MS2109/MS2130, Magewell и т.п.) или по USB в режиме веб-камеры. В карточке свободной камеры выберите «Проф. камера через ноутбук», откройте ссылку для ноутбука в Chrome, нажмите «Начать трансляцию» и разрешите доступ. Выберите в списках карту захвата и источник звука: звук по HDMI тоже приходит через карту захвата. Ноутбук запомнит выбор. На камере поставьте 25p или 50p и отключите вывод меню на HDMI. Ноутбук держите на зарядке, а вкладку не сворачивайте.</li>
        <li>Камеру занимает первый подключившийся телефон — второй по той же ссылке не перехватит эфир. Чтобы поставить другой телефон, нажмите «Отключить телефон».</li>
        <li>Если ссылка попала к посторонним — «Сменить ссылки»: старые перестанут работать, все отключатся, на телефонах и в vMix откройте новые.</li>
        <li>Нажмите на карточку камеры, чтобы открыть картинку под ней, нажмите ещё раз — чтобы скрыть. Открывать можно несколько сразу. Каждая открытая картинка — это ещё один приёмник: телефон кодирует и отдаёт ей отдельный поток, поэтому лишние лучше закрывать.</li>
      </ol>
      <p class="muted">Параметры ссылки для vMix: <code>&amp;codec=vp8</code> — если vMix не показывает картинку (нет H.264), <code>&amp;fit=cover</code> — заполнить кадр без полос, <code>&amp;muted=1</code> — без звука, <code>&amp;debug=1</code> — статистика поверх видео.</p>
    </details>
    <section class="grid">
      ${slots
        .map(
          (s) => `
        <article class="card cam" data-slot="${s.slot}">
          <div class="cam-toggle" role="button" tabindex="0" title="Показать / скрыть картинку">
          <div class="cam-head"><h2></h2><span class="pill">нет камеры</span></div>
          <div class="cam-body">
            <img class="qr" src="${s.qr}" alt="QR-код камеры ${s.slot}" width="140" height="140">
            <div class="cam-info">
              <div class="uptime muted"></div>
              <div class="stats muted"></div>
            </div>
          </div>
          <div class="preview-toggle muted"></div>
          </div>
          <div class="preview" hidden data-out="${esc(previewSrc(s.outUrl))}"></div>
          <div class="row">
            <label class="field grow"><span>Название (видно в студии)</span><input data-name maxlength="40" value="${esc(s.name)}" placeholder="Камера ${s.slot}"></label>
            <label class="field"><span>Что подключено</span><select data-kind>
              <option value="phone"${s.kind === "phone" ? " selected" : ""}>Телефон</option>
              <option value="pro"${s.kind === "pro" ? " selected" : ""}>Проф. камера через ноутбук</option>
            </select></label>
          </div>
          <label class="field"><span data-cam-label></span><div class="copy"><input readonly data-cam-url><button class="btn ghost" data-copy>Копировать</button></div></label>
          <a class="btn ghost" data-open-cam target="_blank" rel="noopener" hidden>Открыть камеру на этом компьютере</a>
          <label class="field"><span>Ссылка для vMix (Web Browser)</span><div class="copy"><input readonly value="${esc(s.outUrl)}"><button class="btn ghost" data-copy>Копировать</button></div></label>
          <button class="btn ghost" data-preview>Показать картинку</button>
          <button class="btn danger" data-kick hidden></button>
        </article>`,
        )
        .join("")}
    </section>`;
  bindLogout();

  for (const s of slots) paintSlot(document.querySelector<HTMLElement>(`.cam[data-slot="${s.slot}"]`)!, s);
  app.querySelectorAll<HTMLElement>(".cam").forEach((card) => {
    const slot = Number(card.dataset.slot);
    const save = async (patch: { name?: string; kind?: SlotKind }) => paintSlot(card, await api<SlotLinks>(`/api/events/${id}/slots/${slot}`, { method: "PATCH", body: JSON.stringify(patch) }));
    card.querySelector<HTMLInputElement>("[data-name]")!.onchange = (e) => save({ name: (e.target as HTMLInputElement).value });
    card.querySelector<HTMLSelectElement>("[data-kind]")!.onchange = (e) => save({ kind: (e.target as HTMLSelectElement).value as SlotKind });
  });

  app.querySelectorAll<HTMLButtonElement>("[data-copy]").forEach((btn) => {
    btn.onclick = async (e) => {
      e.preventDefault();
      const input = btn.previousElementSibling as HTMLInputElement;
      try {
        await navigator.clipboard.writeText(input.value);
      } catch {
        input.select();
        document.execCommand("copy");
      }
      btn.textContent = "Скопировано";
      setTimeout(() => (btn.textContent = "Копировать"), 1500);
    };
  });

  openPreviews.clear();
  app.querySelectorAll<HTMLElement>(".cam-toggle, [data-preview]").forEach((el) => {
    const slot = Number(el.closest<HTMLElement>(".cam")!.dataset.slot);
    const toggle = () => {
      if (!openPreviews.delete(slot)) openPreviews.add(slot);
      syncPreviews();
    };
    el.onclick = toggle;
    el.onkeydown = (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        toggle();
      }
    };
  });
  syncPreviews();
  $("#previews").onclick = () => {
    openPreviews.clear();
    syncPreviews();
  };

  app.querySelectorAll<HTMLButtonElement>("[data-kick]").forEach((btn) => {
    const card = btn.closest<HTMLElement>(".cam")!;
    const slot = card.dataset.slot;
    btn.onclick = async () => {
      const what = KIND_TEXT[kindOf(card)].kickAsk;
      if (!confirm(`Отключить ${what} от камеры ${slot}? Эфир с него прервётся, место освободится для другого устройства.`)) return;
      await api(`/api/events/${id}/kick/${slot}`, { method: "POST" });
    };
  });
  $("#pass-btn").onclick = () => openPasswordForm(id, Boolean(event.hasPassword));
  $("#rotate").onclick = async () => {
    if (!confirm("Сменить все ссылки этой трансляции? Старые QR-коды и ссылки vMix перестанут работать, все телефоны и vMix отключатся — их нужно будет подключить заново по новым ссылкам.")) return;
    await api(`/api/events/${id}/rotate`, { method: "POST" });
    renderEvent(id);
  };

  $("#rename").onclick = async () => {
    const name = prompt("Название трансляции", event.name);
    if (!name) return;
    await api(`/api/events/${id}`, { method: "PATCH", body: JSON.stringify({ name }) });
    renderEvent(id);
  };
  $("#cams").onclick = async () => {
    const n = Number(prompt("Сколько камер (1–8)?", String(event.cameras)));
    if (!n) return;
    await api(`/api/events/${id}`, { method: "PATCH", body: JSON.stringify({ cameras: n }) });
    renderEvent(id);
  };
  $("#delete").onclick = async () => {
    if (!confirm(`Удалить «${event.name}»? Ссылки камер и vMix перестанут работать.`)) return;
    await api(`/api/events/${id}`, { method: "DELETE" });
    location.hash = "#/";
  };

  watchEvent(id);

  clearInterval(clockTimer);
  clockTimer = window.setInterval(() => {
    document.querySelectorAll<HTMLElement>(".cam").forEach((card) => {
      const since = Number(card.dataset.since);
      card.querySelector<HTMLElement>(".uptime")!.textContent = since ? `В сети ${formatUptime(since)}` : "";
    });
  }, 1000);
}

async function route() {
  me = await fetch("/api/me").then((r) => r.json()).catch(() => ({ operator: false }));
  if (!(me as { operator?: boolean }).operator) return renderLogin();
  const m = location.hash.match(/^#\/e\/([A-Za-z0-9_-]+)/);
  try {
    await (m ? renderEvent(m[1]) : renderList());
  } catch (err) {
    if (err instanceof LockedError && m) renderUnlock(m[1]);
    else if ((err as Error).message === "not-found" && m) {
      notice = "Этой трансляции больше нет — её удалили.";
      location.hash = "#/";
    }
    else if ((err as Error).message !== "unauthorized") {
      app.innerHTML = `${header(true)}<p class="error">Не удалось загрузить: ${esc((err as Error).message)}</p>`;
      bindLogout();
    }
  }
}

window.addEventListener("hashchange", route);
// Вкладку открыли снова (могла «спать» в фоне) — список сверяем с сервером.
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible" && !location.hash.startsWith("#/e/") && opSocket) renderList().catch(() => {});
});
route();
